<script lang="ts" setup>
import { Head, Link } from "@inertiajs/vue3";
import HeadingComponent from "../../Components/HeadingComponent.vue";
import {
    entityDecoder,
    formatTrackDistanceHuman,
    formatTrackSpeedLimitHuman,
    formatUnitsHuman,
    getCurrencySymbol,
    getDeviceNavigationAppUrl,
    linkify,
} from "@/Helpers/helperFunctions.js";
import { onMounted, onUnmounted, reactive, ref } from "vue";
import "/node_modules/flag-icons/css/flag-icons.min.css";
import { DateTime } from "luxon";
import { PencilIcon } from "@heroicons/vue/20/solid";
import TrackShareLinks from "@/Components/TrackShareLinks.vue";
import { usePage } from "@inertiajs/vue3";
import TrackEntryFeeFormModal from "../../Components/TrackEntryFeeFormModal.vue";
import { Event } from "@/types";

interface Props {
    track: Event;
    userOrder: object | null;
    track_is_processing: boolean;
}

import { ArrowTopRightOnSquareIcon } from "@heroicons/vue/24/solid/index.js";

import TrackPublicWrapper from "@/Pages/TrackPublic/TrackPublicWrapper.vue";
import TrackPublicMap from "@/Pages/TrackPublic/Components/TrackPublicMap.vue";
import Modal from "@/Components/Modal.vue";
import Button from "@/Components/Button.vue";
import TooltipIcon from "@/Components/TooltipIcon.vue";
import eventBus from "@/Helpers/eventBus";
import TrackDownload from "@/Components/TrackDownload.vue";
import SendMessageModal from "@/Components/SendMessageModal.vue";
import NavigateByBadge from "@/Components/NavigateByBadge.vue";

const props = withDefaults(defineProps<Props>(), {});

onMounted(() => {});

onUnmounted(() => {});

const myImageModalProps = reactive({
    modalImageSrc: "",
    showModal: false,
});

function showImageModal(src) {
    myImageModalProps.modalImageSrc = src;
    myImageModalProps.showModal = true;
}

function closeImageModal() {
    myImageModalProps.showModal = false;
}

function showTrackEntryFeeFormModal() {
    eventBus.$emit("showTrackEntryFeeFormModal", {
        formData: {
            full_name: usePage().props.user?.name,
            email: usePage().props.user?.email,
            country: usePage().props.user?.country,
        },
    });
}

function showSendMessageModal() {
    if (!usePage().props.user?.email) {
        alert("Please log in to send a message to the track creator/owner!");
        return;
    }

    eventBus.$emit("showSendMessageModal");
}
</script>

<template>
    <TrackPublicWrapper>
        <Head>
            <title>Track: {{ $page.props.track.name }}</title>

            <meta
                property="og:title"
                head-key="og:title"
                :content="`Track: ${$page.props.meta_track_name}`"
            />

            <meta
                name="description"
                head-key="description"
                :content="`Track information: ${$page.props.meta_track_name}`"
            />
            <meta
                property="og:description"
                :content="`Track information: ${$page.props.meta_track_name}`"
            />
        </Head>
        <div class="flex w-full md:max-w-xl lg:basis-2/3 xl:basis-1/2 2xl:basis-2/5">
            <div class="w-full space-y-4">
                <div
                    v-if="track_is_processing"
                    class="mb-4 rounded-lg border border-red-300 bg-red-50 p-4 text-red-800"
                    role="alert"
                >
                    <div class="flex items-center">
                        <svg
                            class="-ml-1 mr-3 size-12 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <span class="sr-only">Info</span>
                        <h3 class="text-lg font-medium">This Track is currently processing....</h3>
                    </div>
                    <div class="mt-2 text-sm">Please wait a couple of seconds and refresh the page</div>
                </div>
                <div
                    v-if="track.visibility === 'private'"
                    class="mb-4 rounded-lg border border-red-300 bg-red-50 p-4 text-red-800"
                    role="alert"
                >
                    <div class="flex items-center">
                        <svg
                            class="me-2 size-4 shrink-0"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                            />
                        </svg>
                        <span class="sr-only">Info</span>
                        <h3 class="text-lg font-medium">
                            This Track is currently private. Only the Track owner and team administrators can
                            access this page.
                        </h3>
                    </div>
                    <div class="mt-2 text-sm">
                        Once you are satisfied with all details, you can choose to make the Track public or
                        unlisted.
                    </div>
                    <div
                        v-if="track.event_id"
                        class="mt-2 text-sm"
                    >
                        Event tracks, regardless of their visibility status, are exclusively accessible to
                        "accepted" event competitors according to their scheduled date and time, and section
                        settings.
                    </div>
                </div>

                <div
                    v-if="track.event_id"
                    class="mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4 text-blue-800"
                    role="alert"
                >
                    <div class="flex items-center">
                        <svg
                            class="me-2 size-4 shrink-0"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                            />
                        </svg>
                        <span class="sr-only">Info</span>
                        <h3 class="text-lg font-medium">This Track belongs to the Event</h3>
                    </div>
                    <div class="mt-2 text-sm">
                        <Link
                            :href="
                                route('event.public.information', {
                                    hashid: track.event.hashid,
                                    seo_slug: track.event.seo_slug,
                                })
                            "
                        >
                            {{ track.event.name }}
                        </Link>
                    </div>
                </div>

                <div
                    v-if="track.event_id"
                    class="mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4 text-blue-800"
                    role="alert"
                >
                    <div class="flex items-center">
                        <svg
                            class="me-2 size-4 shrink-0"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                            />
                        </svg>
                        <span class="sr-only">Info</span>
                        <h3 class="text-lg font-medium">
                            Event organizers (and anyone else with a link or PIN) can easily test-ride this
                            track before the event. Replays, leaderboards, and any penalties incurred will be
                            available for you to analyze independently of event results. Learn more:
                            <a href="/faq/how-to-test-ride-event-tracks-and-itinerary-15"
                                >How to test-ride event tracks?</a
                            >
                        </h3>
                    </div>
                </div>

                <HeadingComponent class="rounded-lg bg-white p-4 shadow-lg sm:px-6">
                    <template #name>
                        Track information&nbsp;<span
                            class="inline-flex rounded-full px-2 text-sm font-semibold"
                            :class="{
                                'bg-green-100 text-green-800': track.visibility === 'public',
                                'bg-red-100 text-red-800': track.visibility === 'private',
                                'bg-slate-100 text-slate-800': track.visibility === 'unlisted',
                            }"
                        >
                            {{ track.visibility }}</span
                        >
                        {{
                            $page.props.user && $page.props.user.can_impersonate
                                ? ` (track_id: ${$page.props.track.id} track_version_id: ${$page.props.track.latestVersion.id} user_id: ${$page.props.track.user_id})`
                                : ""
                        }}
                    </template>
                    <template #description>{{ track.name }} (v.{{ track.latestVersion.version }})</template>
                </HeadingComponent>
                <HeadingComponent
                    v-if="$page.props.can.update_track"
                    class="rounded-lg bg-orange-50 p-4 shadow-lg sm:px-6"
                >
                    <template #name>Track owner tools</template>
                    <template #description>
                        <div class="pt-4">
                            <Button
                                :href="
                                    route('track.edit.general', {
                                        pin: $page.props.track.pin,
                                        seo_slug: $page.props.track.seo_slug,
                                    })
                                "
                                class="h-6"
                                variant="outline"
                            >
                                <PencilIcon
                                    class="-ml-0.5 mr-1.5 size-4"
                                    aria-hidden="true"
                                />
                                Edit general info
                            </Button>
                        </div>
                        <TrackShareLinks
                            class="mt-4 border-t border-orange-200 pt-4"
                            :pin="$page.props.track.pin"
                            :seo-slug="$page.props.track.seo_slug"
                        />
                    </template>
                </HeadingComponent>

                <div
                    v-if="!track_is_processing"
                    class="flex flex-col"
                >
                    <div class="overflow-hidden rounded-lg bg-white shadow-lg">
                        <div class="border-t border-gray-100">
                            <dl class="divide-y divide-gray-100">
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Navigation by</dt>
                                    <dd
                                        class="mt-1 text-sm capitalize leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        <NavigateByBadge :navigate-by="track.navigate_by" />
                                    </dd>
                                </div>
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Entry fee</dt>
                                    <dd
                                        v-if="track.pricing !== 'paid'"
                                        class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        Free
                                    </dd>
                                    <dd
                                        v-if="track.pricing === 'paid'"
                                        class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        {{ getCurrencySymbol(track.t_entry_fee_currency)
                                        }}{{ track.t_entry_fee.toFixed(2) }} for
                                        {{ track.access_duration_days }}
                                        {{
                                            track.access_duration_days.toString()[
                                                track.access_duration_days.toString().length - 1
                                            ] == "1"
                                                ? "day"
                                                : "days"
                                        }}
                                        <TooltipIcon classes="size-5">
                                            <div class="max-w-sm">
                                                How many days will the track be available to ride:<br />
                                                <br />
                                                <strong
                                                    >You can pay the entry fee now and ride the track later. The
                                                    track access duration period begins only when you download
                                                    it in the RBR app.</strong
                                                >
                                                <br />
                                                <br />
                                                You can ride the track as many times as you want during this
                                                period.<br />
                                                <br />
                                                All rides/attempts will be timed, analyzed, and included in the
                                                leaderboard and replay.<br />
                                                <br />
                                                You can press the "restart" button in the app to start the track
                                                again. It is possible to mask your ride identification info from
                                                the leaderboard and replay.
                                                <br />
                                                <br />
                                                P.S. Unused entries and entry fees will expire four months after
                                                the entry fee payment.
                                            </div>
                                        </TooltipIcon>
                                        &nbsp;
                                        <Button
                                            v-if="!track.is_download_disabled"
                                            class="h-6"
                                            @click="showTrackEntryFeeFormModal()"
                                            >Buy access
                                        </Button>
                                    </dd>
                                </div>
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">
                                        PIN
                                        <TooltipIcon
                                            classes="size-5"
                                            href="/faq/what-is-a-trackroadbook-pin-code-1"
                                        >
                                            What is a Track PIN code?
                                        </TooltipIcon>
                                    </dt>
                                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        <span :title="track.id">{{ track.pin }}</span
                                        >&nbsp;
                                        <TrackDownload
                                            :track="track"
                                            :user_order="$page.props.userOrder"
                                        />
                                        <span
                                            v-if="track.is_download_disabled"
                                            class="mt-1 block font-medium text-red-600"
                                        >
                                            The track owner has temporarily disabled this track. It cannot be
                                            downloaded or bought right now.
                                            <TooltipIcon classes="size-5">
                                                <div class="max-w-sm">
                                                    The track owner closed this track for some time, for example
                                                    the road is flooded, or there are roadworks on it.<br />
                                                    <br />
                                                    Please come back later, or ask the track owner when the
                                                    track opens again.
                                                </div>
                                            </TooltipIcon>
                                        </span>
                                    </dd>
                                </div>
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Description</dt>
                                    <dd
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        <article
                                            class="prose whitespace-pre-wrap break-words"
                                            v-html="linkify(entityDecoder(track.description))"
                                        ></article>
                                    </dd>
                                </div>
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Track creator</dt>
                                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                        {{
                                            track.user && track.user.track_creator_brand
                                                ? track.user.track_creator_brand
                                                : "-"
                                        }}
                                        &nbsp;
                                        <Button
                                            class="h-6"
                                            @click="showSendMessageModal()"
                                            >Contact creator
                                        </Button>
                                    </dd>
                                </div>
                                <div
                                    v-if="track.country_full"
                                    class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                                >
                                    <dt class="text-sm font-medium text-gray-900">Country</dt>
                                    <dd
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        <template v-if="track.country">
                                            <span
                                                :class="`border text-xl fi fi-${track.country.toLowerCase()}`"
                                            ></span>
                                            &nbsp;
                                            {{ track.country_full }}
                                        </template>
                                    </dd>
                                </div>
                                <div
                                    v-if="track.latestVersion.start_location"
                                    class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                                >
                                    <dt class="text-sm font-medium text-gray-900">Start location</dt>
                                    <dd
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        <a
                                            :href="
                                                getDeviceNavigationAppUrl(track.latestVersion.start_location)
                                            "
                                            target="_blank"
                                        >
                                            {{
                                                track.latestVersion.start_location.coordinates
                                                    .join(",")
                                                    .split(",")
                                                    .reverse()
                                                    .join(", ")
                                            }}
                                            <ArrowTopRightOnSquareIcon
                                                class="inline size-5"
                                            ></ArrowTopRightOnSquareIcon>
                                        </a>
                                    </dd>
                                </div>
                                <div
                                    v-if="track.latestVersion.end_location"
                                    class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                                >
                                    <dt class="text-sm font-medium text-gray-900">End location</dt>
                                    <dd
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        <a
                                            :href="getDeviceNavigationAppUrl(track.latestVersion.end_location)"
                                            target="_blank"
                                        >
                                            {{
                                                track.latestVersion.end_location.coordinates
                                                    .join(",")
                                                    .split(",")
                                                    .reverse()
                                                    .join(", ")
                                            }}
                                            <ArrowTopRightOnSquareIcon
                                                class="inline size-5"
                                            ></ArrowTopRightOnSquareIcon>
                                        </a>
                                    </dd>
                                </div>
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Units</dt>
                                    <dd
                                        v-if="track.units"
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        {{ formatUnitsHuman(track.units) }}
                                    </dd>
                                </div>
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Length</dt>
                                    <dd
                                        v-if="track.latestVersion.distance > 0"
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        {{ formatTrackDistanceHuman(track) }}
                                    </dd>
                                </div>
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">
                                        {{ track.navigate_by === "gpx" ? "Waypoints" : "Instructions" }}
                                    </dt>
                                    <dd
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        {{ track.latestVersion.classSectionIndividual.instructions_count }}
                                    </dd>
                                </div>
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Speed limit</dt>
                                    <dd
                                        v-if="track.latestVersion.distance > 0"
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        {{ formatTrackSpeedLimitHuman(track) }}
                                    </dd>
                                </div>
                                <!--                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">
                                        Maximum time allowed
                                        <TooltipIcon
                                            classes="size-5"
                                            href="/faq/track-leaderboard-66"
                                        >
                                            What is the maximum time allowed?
                                        </TooltipIcon>
                                    </dt>
                                    <dd
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        ccc
                                    </dd>
                                </div>-->
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Updated at</dt>
                                    <dd
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        {{ new Date(track.latestVersion.updated_at).toLocaleDateString() }}
                                        (v.{{ track.latestVersion.version }})
                                        <br />
                                        ({{ DateTime.fromISO(track.latestVersion.updated_at).toRelative() }})
                                    </dd>
                                </div>
                                <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt class="text-sm font-medium text-gray-900">Created at</dt>
                                    <dd
                                        class="mt-1 text-sm tabular-nums leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                                    >
                                        {{ new Date(track.created_at).toLocaleDateString() }}
                                        <br />
                                        ({{ DateTime.fromISO(track.created_at).toRelative() }})
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>

                <div
                    v-if="$page.props.entity_images.length > 0"
                    class="h-auto max-w-full rounded-lg border bg-white p-3 shadow-lg sm:p-4"
                >
                    <div class="space-y-4">
                        <img
                            v-for="logo in $page.props.entity_images"
                            :key="logo"
                            :src="logo"
                            class="cursor-pointer rounded-md"
                            alt="Track poster"
                            @click="showImageModal(logo)"
                        />
                    </div>
                </div>
            </div>
        </div>

        <Modal
            :show="myImageModalProps.showModal"
            max-width="min"
            @close="closeImageModal"
        >
            <div @click="closeImageModal()">
                <img
                    :src="myImageModalProps.modalImageSrc"
                    class="max-h-full rounded border bg-white p-1 shadow-lg sm:max-h-screen sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg"
                />
            </div>
        </Modal>

        <template #map>
            <TrackPublicMap
                v-if="$page.props.track.latestVersion.distance > 0"
                class="content-full-screen-height-mt-4 hidden flex-1 rounded-lg bg-white shadow-lg lg:flex"
            />
        </template>

        <TrackEntryFeeFormModal></TrackEntryFeeFormModal>
        <SendMessageModal
            v-if="usePage().props.user"
            entity-name="track"
        ></SendMessageModal>
    </TrackPublicWrapper>
</template>
